import { CircularProgress, Dialog } from '@mui/material'
import { LocalizationProvider as MuiLocalizationProvider } from '@mui/x-date-pickers'
// import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { isSupported } from 'firebase/messaging/sw'
import dayjs from 'dayjs'
import React, { Suspense, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
// PublicLiveSessionPage is lazy-loaded below to avoid blocking main bundle with heavy Agora SDK (~800KB+)
// This is critical for initial page load performance across the entire app
// The skeleton is imported directly (it's lightweight) for immediate display
import PublicLiveSessionPageSkeleton from 'src/consumer/components/PublicLiveSessionPage/PublicLiveSessionPageSkeleton'
// Error boundary for graceful error handling when lazy chunks fail to load
import PublicLiveSessionPageErrorBoundary from 'src/consumer/components/PublicLiveSessionPage/PublicLiveSessionPageErrorBoundary'
import { ThemeProvider } from 'styled-components'
import { v4 as uuidv4 } from 'uuid'
import { default as BusinessProtectedRoute } from './business/views/pages/login/ProtectedRoute'
import { EmptyLoadingScreen } from './consumer/components/Loaders'
import RouteChunkProgress from './consumer/components/Loaders/RouteChunkProgress'
import { bookedPageViewTypes } from './consumer/components/MyLiveSessions/my-live-sessions-utils'
import { StyledPushNotificationDialog } from './consumer/components/PushNotificationsContent/styledPushNotificationsComponent'
import { rememberLiveSessionReturnUrl } from './consumer/components/LiveSessionPage/utils/liveSessionExitNavigation'
import ApiDebugStatusBadge from './components/AppUpdate/ApiDebugStatusBadge'
import { RestCustomServerChange } from './components/AppUpdate/RestCustomServerChange'
import { APP_CONSTANTS, REDUX_STATUS_LOADING } from './consumer/constants'
import {
  LocalizationContext,
  LocalizationContextProvider,
} from './consumer/context/LocalizationContext'
import { NotificationContextProvider } from './consumer/context/NotificationContextProvider'
import {
  ThemeDataContext,
  ThemeDataContextProvider,
} from './consumer/context/ThemeDataContextProvider'
import {
  UnsecureSetupContext,
  UnsecureSetupContextProvider,
} from './consumer/context/UnsecureSetupContext'
import { ParallelDataProviders } from './consumer/context/ParallelDataProviders'
import SEOComponent from './shared/seo/SEOComponent'
import { GenericHideChildrenOnPrintWrapper } from './consumer/layout/ConsumerLayoutStyles'
import {
  getSessionCreationTime,
  getSessionId,
  setSessionCreationTime,
  setSessionId,
} from './consumer/services/localstorageServices'
import { getApiDebugQueryConfig, resolveApiDomainConfig } from './consumer/services/apiDomainConfig'
import { AuthContextProvider } from './context/AuthContext'
import { FCMContext, FCMProvider, setNotificationsAreDeniedLocal } from './context/FCMProvider'
import { getIpAddress } from './helper'
import { getImageLink } from './mentor/helpers/utilityFunctions'
// Import base CSS synchronously (critical path - needed everywhere)
import './scss/base.scss'
// Route-specific CSS is loaded dynamically via cssLoader utility
import { loadCSSForRoute } from './utils/cssLoader'
import SupportPortalProtectedRoute from './supportPortal/SupportPortalProtectedRoute'
import IpdPortalProtectedRoute from './ipd/IpdPortalProtectedRoute'
import ProtectedRoute, { isTokenInvalidOrAbsent } from './views/pages/login/ProtectedRoute'
import { createPortal } from 'react-dom'
import ChamberScreenSignallingSnackBarProvider from 'src/mentor/components/MentorClinicSomeChamberAutoJoiningPage/components/ChamberScreenSignallingSnackBarProvider'
import DocSideContextProvider, { DocSideHelper } from 'src/context/DocSideContextProvider'
import { ClinicDataProvider } from 'src/context/ClinicDataContext'
import ClinicLayoutProvider from 'src/clinic/context/ClinicLayoutProvider'
import { ROLE_TYPES } from 'src/consumer/constants'
import { setupAxiosInterceptors } from 'src/services/axiosInterceptor'
import Global401LoginModal from 'src/components/Global401LoginModal'
import { isStandaloneAppMode } from 'src/components/AppUpdate/appRefreshUtils'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import { getProfileData } from 'src/reducers/profileData/services/profileData'
import { normalizeProfileData } from 'src/reducers/profileData/normalizeProfileData'
import { selectProfileData, setProfileData } from 'src/reducers/profileData/profileDataReducer'
import { persistPortalAvailabilityOverridesFromAuthData } from 'src/shared/config/portalAvailabilityRules'
import HeartbeatManager from 'src/mentor/layout/checkinHeartbeatManager'
import EmrCheckInIdleManager from 'src/clinic/components/CheckIn/EmrCheckInIdleManager'
// Containers
const TermsPage = React.lazy(() => import('./consumer/components/ServiceTermsAndLegal/TermsPage'))
const PublicApplyApp = React.lazy(() => import('./views/applyPublic/PublicApplyApp'))
const GuestMemberProfile = React.lazy(() =>
  import('./consumer/components/MentalHealthMedicalBoardComponents/boardMember/GuestMemberProfile'),
)
const PrivacyPolicyOne = React.lazy(() =>
  import('./consumer/components/ServiceTermsAndLegal/PrivacyPolicyOne'),
)
const AboutPage = React.lazy(() => import('./consumer/components/ServiceTermsAndLegal/AboutPage'))
const PrivacyPage = React.lazy(() =>
  import('./consumer/components/ServiceTermsAndLegal/PrivacyPage'),
)
const CopyOfPrivacyPolicy = React.lazy(() =>
  import('./consumer/components/ServiceTermsAndLegal/CopyOfPrivacyPolicy'),
)
const MedicalBoardMemberPage = React.lazy(() =>
  import('./consumer/components/ServiceTermsAndLegal/MedicalBoardMemberPage'),
)
const MentalHealthMedicalBoard = React.lazy(() =>
  import('./consumer/components/ServiceTermsAndLegal/MentalHealthMedicalBoard'),
)
const LegalPage = React.lazy(() => import('./consumer/components/ServiceTermsAndLegal/LegalPage'))
const PublicSessionCodeJoinPage = React.lazy(() =>
  import('./consumer/components/PublicLiveSessionPage/PublicSessionCodeJoinPage'),
)

const BookingSuccessfulPage = React.lazy(() =>
  import('./consumer/components/BookingSuccessfulPage'),
)

const CookiePolicy = React.lazy(() =>
  import('./consumer/components/ServiceTermsAndLegal/CookiePolicy'),
)
const InstructorTerms = React.lazy(() =>
  import('./consumer/components/ServiceTermsAndLegal/InstructorTerms'),
)

const ManageDataAndAccount = React.lazy(() =>
  import('./consumer/components/ManageAccountData/ManageDataAndAccount'),
)
const GiftCard = React.lazy(() => import('./GiftCard/components/GiftCardHomepage'))
const GiftCardRedeem = React.lazy(() => import('./GiftCard/components/GiftCardRedeem'))
// const ProfileLayout = React.lazy(() => import('./layout/ProfileLayout'))

// Pages
const Login = React.lazy(() => import('./views/pages/login/signIn'))
const MobileSignIn = React.lazy(() => import('./consumer/components/SignIn/MobileSignIn'))
const ForgotPassword = React.lazy(() => import('./views/pages/login/forgotPassword'))
const ChangePassword = React.lazy(() => import('./views/pages/login/changePassword'))
const Page404 = React.lazy(() => import('./views/pages/page404/Page404'))
const ResetStorage = React.lazy(() => import('./views/pages/ResetStorage'))
// const Page500 = React.lazy(() => import('./views/pages/page500/Page500'))
const Onboarding = React.lazy(() => import('./consumer/components/Onboarding'))
const HelpCenter = React.lazy(() => import('./consumer/components/HelpCenter'))
const DownloadAppPage = React.lazy(() => import('./consumer/components/DownloadApp/index'))
const LeaderboardPageWebview = React.lazy(() =>
  import('./consumer/components/LeaderboardPage/indexWebview'),
)
const MyLiveSessionsWrapperForWebview = React.lazy(() =>
  import('./consumer/components/MyLiveSessions/MyLiveSessionsWrapperForWebview'),
)

const PatientHomeWrapperForWebview = React.lazy(() =>
  import('./consumer/components/TilesPagePatient/PatientHomeWrapperForWebview'),
)

const HelpCenterCategoryPage = React.lazy(() =>
  import('./consumer/components/HelpCenter/HelpCenterCategoryPage'),
)

const ConsumerLayout = React.lazy(() => import('./consumer/layout/ConsumerLayout'))
const ClinicWalkinPage = React.lazy(() =>
  import('src/clinic/components/ClinicBrandPages/ClinicWalkinPage'),
)
const GoogleBusinessOAuthCallback = React.lazy(() =>
  import('src/shared/googleBusiness/GoogleBusinessOAuthCallback'),
)
const ReviewRedirectPage = React.lazy(() => import('src/shared/reviewLink/ReviewRedirectPage'))
const ReviewFormPage = React.lazy(() => import('src/shared/reviewForm/ReviewFormPage'))
const CareTrackQuestionnairePage = React.lazy(() => import('src/shared/careTrackQuestionnaire/CareTrackQuestionnairePage'))
const PublicCertificateValidationPage = React.lazy(() =>
  import('src/consumer/components/PublicCertificateValidation/PublicCertificateValidationPage'),
)
const SupportOohPreview = React.lazy(() =>
  import('src/clinic/components/ClinicCounterScreen/SupportOohPreview'),
)
const ClinicOwnerLayout = React.lazy(() => import('src/clinic/layout/ClinicOwnerLayout'))
const CALayout = React.lazy(() => import('src/clinic/layout/CALayout'))
const KioskLayout = React.lazy(() => import('src/chamberScreen/layout/KioskLayout'))
const KioskRoutesHandler = React.lazy(() => import('src/chamberScreen/layout/KioskRoutesHandler'))
const DisplayRoutesHandler = React.lazy(() => import('src/chamberScreen/layout/DisplayRoutesHandler'))
const SupportPortalLayout = React.lazy(() => import('src/supportPortal/layout/SupportPortalLayout'))
// more layouts here: pharmacist, therapist, doctor
const MentorLayout = React.lazy(() => import('src/mentor/layout'))
const BusinessLayout = React.lazy(() => import('src/business/layout/BusinessLayout'))
const PatientPortalLayout = React.lazy(() => import('src/patient/layout/PatientPortalLayout'))
const PatientPortalProtectedRoute = React.lazy(() =>
  import('src/patient/PatientPortalProtectedRoute'),
)
const IpdLayout = React.lazy(() => import('src/ipd/layout/IpdLayout'))

//activity plan pages
const ActivityPlanPageWebView = React.lazy(() =>
  import('./consumer/components/ActivityPlansPage/ActivityPlanWebViewWrapper'),
)

const ActivityPlanListingPageWebview = React.lazy(() =>
  import('./consumer/components/ActivityPlanPublicListing/ActivityPlanPublicListing'),
)

//User activity plan pages

const UserActivityPageWebview = React.lazy(() =>
  import('./consumer/components/UserActivityPage/UserActivityWebviewWrapper'),
)

const UserActivityListingPageWebview = React.lazy(() =>
  import('./consumer/components/UserActivityListingPage/UserActivityListingPageWebviewWrapper'),
)
const AccountInvitePage = React.lazy(() => import('./accountInvite'))
const EmailVerification = React.lazy(() => import('./consumer/components/Account/EmailVerify.jsx'))

const AgoraSignallingComponentForChamberScreens = React.lazy(() =>
  import('./components/AgoraSingallingComponent/AgoraSignallingComponentForChamberScreens'),
)
const AgoraSignallingComponentForAumhumScreens = React.lazy(() =>
  import('./components/AgoraSingallingComponent/AgoraSignallingComponentForAumhumScreens'),
)

const AgoraSignallingComponentForMentors = React.lazy(() =>
  import('./components/AgoraSingallingComponent/AgoraSignallingComponentForMentors'),
)
const AgoraSignallingComponentForSupportPortalUsers = React.lazy(() =>
  import('./components/AgoraSingallingComponent/AgoraSignallingComponentForSupportPortalUsers'),
)
const EmrDebugCommandAgent = React.lazy(() => import('./emr/debug/EmrDebugCommandAgent'))

const LazyPushNotificationComponent = React.lazy(
  () => import('./consumer/components/PushNotificationsContent/PushNotificationComponent'), // Adjust the path accordingly
)

const BookingRequestNavigationAndToastComponent = React.lazy(() =>
  import('./views/pages/BookingRequestNavigationAndToastComponent'),
)
const FloatingIntercomButton = React.lazy(() =>
  import('./components/FloatingIntercom/FloatingIntercomButton'),
)
const AppUpdatePopup = React.lazy(() => import('./components/AppUpdate/AppUpdatePopup'))

// CRITICAL: PublicLiveSessionPage is lazy-loaded to prevent blocking the main bundle
// This page loads heavy Agora SDK dependencies (~800KB+) for video conferencing
// By lazy loading, we ensure these dependencies only load when users visit /v/:sessionSlug
// The skeleton provides immediate visual feedback while the chunk loads
const PublicLiveSessionPage = React.lazy(() =>
  import('src/consumer/components/PublicLiveSessionPage/PublicLiveSessionPage')
)

// GTM/GA4 removed on purpose — do not re-add it here.
//
// The loader that used to live here injected container GTM-PB5QCGXH, gated on
// `localStorage.token`, from the app shell. Two problems:
//
// 1. It only ran for LOGGED-IN users, so it measured the clinical app and skipped the entire
//    acquisition funnel. That gate arrived with "Performance and SEO optimizations" (4d77cfd9c,
//    2025-12-03), which moved GTM out of index.html — i.e. an SEO change silently switched
//    analytics off for the anonymous SEO traffic it was optimising.
// 2. It pushed no custom events at all, so all it did was report GA4's automatic page_view —
//    whose page_location is the full URL. Authenticated URLs carry clinical identifiers
//    (`?careTrack=adult-adhd-treatment-start` from CareTracksWorkspace, `?patientId=` in the
//    support portal). CareTracksWorkspace no longer WRITES `careTrackPatient=<id>` into the
//    query string — the selected patient id lives in sessionStorage now — but it still ACCEPTS
//    inbound `careTrackPatient` deep links, so a shared/bookmarked link can still pair a patient
//    id with their condition. For a psychiatry provider that is PHI being sent to a third
//    party, with no consent gate anywhere in the app.
//
// If marketing analytics is wanted again, it belongs on public/logged-out routes only (the
// opposite of the old gate), behind consent, and never on /mentor-home, /support-home,
// /clinic-home, /patient-home or /l/*. Container GTM-PB5QCGXH and property G-2TYB0QV57V were
// left intact — they may hold Ads/remarketing tags.

const LiveSessionReturnTracker = () => {
  const location = useLocation()

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    rememberLiveSessionReturnUrl(location, window.sessionStorage)
  }, [location])

  return null
}

// Pages that fully unmount the floating intercom widget. The mentor clinic-portal tabs are
// intentionally NOT listed here: they keep the widget mounted so the left-menu "Help & Calls"
// button can open the panel via the aumhum:open-intercom event, while the floating button itself
// is hidden in-component (see hideFloatingFab in FloatingIntercomButton).
const FLOATING_INTERCOM_DISABLED_PATHS = new Set([])

const RouteAwareFloatingIntercom = () => {
  const location = useLocation()
  const { authToken } = useContext(ProfileContext)
  const isStandaloneInstalledApp = isStandaloneAppMode()
  const hasValidAuthToken =
    !isTokenInvalidOrAbsent(authToken) && !isTokenInvalidOrAbsent(localStorage.getItem('token'))

  if (!hasValidAuthToken) {
    return null
  }

  if (!isStandaloneInstalledApp && FLOATING_INTERCOM_DISABLED_PATHS.has(location.pathname)) {
    return null
  }

  // Kiosk + Display (OOH) screens are unattended public-facing surfaces — no
  // floating support/intercom widget belongs there (prefix match since the
  // paths are dynamic, e.g. /display/:location/:mentor).
  if (location.pathname.startsWith('/kiosk/') || location.pathname.startsWith('/display/')) {
    return null
  }

  return <FloatingIntercomButton />
}

const WALKIN_AUTH_SETUP_CONTEXT_VALUE = {
  unSecureSetupLoading: false,
  unSecureSetupError: null,
  unSecureSetupData: {
    loginPolicy: {
      login: {
        mobile: true,
        usernamePassword: true,
        google: true,
        facebook: false,
        apple: false,
      },
    },
  },
}

const ClinicWalkinRouteFallback = () => (
  <main
    style={{
      width: '100%',
      minHeight: '100svh',
      margin: 0,
      padding: '16px',
      background: '#f7f8fb',
      fontFamily: 'Outfit, Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#111827',
    }}
  >
    <div
      style={{
        width: '100%',
        maxWidth: 420,
        padding: 18,
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        background: '#ffffff',
      }}
    >
      <div style={{ fontSize: 13, color: '#6b7280', textAlign: 'center' }}>
        Clinic walk-in booking
      </div>
      <div
        style={{
          width: 56,
          height: 56,
          margin: '18px auto 14px',
          borderRadius: 8,
          background: '#eef2ff',
        }}
      />
      <div
        style={{
          height: 18,
          width: '68%',
          margin: '0 auto',
          borderRadius: 4,
          background: '#e5e7eb',
        }}
      />
      <div
        style={{
          height: 12,
          width: '42%',
          margin: '10px auto 0',
          borderRadius: 4,
          background: '#eef2f7',
        }}
      />
      <div style={{ marginTop: 18, display: 'grid', gap: 8 }}>
        <div style={{ height: 48, borderRadius: 8, background: '#f3f4f6' }} />
        <div style={{ height: 76, borderRadius: 8, background: '#f9fafb' }} />
      </div>
    </div>
  </main>
)

const ClinicWalkinStandaloneProfileProvider = ({ children }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const profileDataState = useSelector(selectProfileData)
  const profileData = profileDataState?.data
  const loading = profileDataState?.status === REDUX_STATUS_LOADING
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('token'))

  const syncAuthTokenFromStorage = useCallback(() => {
    const storageAuthToken = localStorage.getItem('token')
    setAuthToken((currentAuthToken) =>
      storageAuthToken !== currentAuthToken ? storageAuthToken : currentAuthToken,
    )
  }, [])

  useEffect(() => {
    syncAuthTokenFromStorage()
    window.addEventListener('storage', syncAuthTokenFromStorage)
    return () => window.removeEventListener('storage', syncAuthTokenFromStorage)
  }, [syncAuthTokenFromStorage])

  const fetchProfileData = useCallback(
    ({ _forceRefresh = false } = {}) => {
      syncAuthTokenFromStorage()
      return dispatch(getProfileData({ navigate, _forceRefresh }))
    },
    [dispatch, navigate, syncAuthTokenFromStorage],
  )

  useEffect(() => {
    if (!isTokenInvalidOrAbsent(authToken) && !profileData) {
      fetchProfileData()
    }
  }, [authToken, fetchProfileData, profileData])

  const handleSetProfileData = useCallback(
    (data) => {
      const normalizedProfileData = normalizeProfileData(data)
      dispatch(setProfileData(normalizedProfileData))

      if (normalizedProfileData?.authorities) {
        localStorage.setItem('authorities', JSON.stringify(normalizedProfileData.authorities))
      }
      persistPortalAvailabilityOverridesFromAuthData(normalizedProfileData)
      syncAuthTokenFromStorage()
    },
    [dispatch, syncAuthTokenFromStorage],
  )

  const profileValue = useMemo(
    () => ({
      profileData,
      setProfileData: handleSetProfileData,
      authToken,
      refreshProfileData: fetchProfileData,
      profileDataLoading: loading,
    }),
    [authToken, fetchProfileData, handleSetProfileData, loading, profileData],
  )

  return <ProfileContext.Provider value={profileValue}>{children}</ProfileContext.Provider>
}

const ClinicWalkinStandaloneApp = () => (
  <Suspense fallback={<ClinicWalkinRouteFallback />}>
    <AuthContextProvider>
      <UnsecureSetupContext.Provider value={WALKIN_AUTH_SETUP_CONTEXT_VALUE}>
        <ClinicWalkinStandaloneProfileProvider>
          <Routes>
            <Route path="/clinic-walkin/:token" element={<ClinicWalkinPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <SEOComponent
            title="Clinic walk-in booking | AUMHUM"
            description="Book your clinic walk-in appointment."
            image={APP_CONSTANTS.APP_LOGO_LINK}
          />
          {createPortal(
            <GenericHideChildrenOnPrintWrapper>
              <ToastContainer
                position="top-center"
                autoClose={5000}
                hideProgressBar={true}
                newestOnTop
                closeOnClick
                pauseOnFocusLoss
                draggable
                pauseOnHover
              />
            </GenericHideChildrenOnPrintWrapper>,
            document.body,
          )}
        </ClinicWalkinStandaloneProfileProvider>
      </UnsecureSetupContext.Provider>
    </AuthContextProvider>
  </Suspense>
)

const AppRouteTree = ({ apiDebugQueryConfig, debugMode, onExitDebugMode }) => {
  const location = useLocation()

  if (location.pathname.startsWith('/clinic-walkin/')) {
    return <ClinicWalkinStandaloneApp />
  }

  return (
    <>
      <LiveSessionReturnTracker />
      {/*
        Suspense fallback was the fullscreen EmptyLoadingScreen (logo +
        "Personalizing your experience" overlay) which flashed on every
        React.lazy route navigation — intrusive on public marketing
        pages. Swapped for a thin top progress bar (a la YouTube)
        that communicates loading without yanking the visitor into a
        loading screen. Chunks that resolve fast barely show it; slow
        chunks get a clear hint. Authenticated portals also benefit.
      */}
      <Suspense fallback={<RouteChunkProgress />}>
        <AuthContextProvider>
          <ParallelDataProviders>
            <UnsecureSetupContextProvider>
              <LocalizationContextProvider>
                <ThemeDataContextProvider>
                  <MuiLocalizationProvider dateAdapter={AdapterDayjs}>
                    <>
                      <FCMProvider>
                        <ChamberScreenSignallingSnackBarProvider>
                          <NotificationContextProvider>
                            <DocSideContextProvider>
                              <AppUpdatePopup>
                                {/* One route-aware instance owns check-in heartbeats for every
                                    EMR surface, including practitioner sessions on /mentor-home/l/*. */}
                                <HeartbeatManager />
                                <EmrCheckInIdleManager />
                                <RenderRoutes />
                                {/* Own boundaries, fallback={null}: both of these are lazy and
                                    non-critical chrome, but they are siblings of <RenderRoutes />.
                                    Sharing the app's top-level Suspense means either one suspending
                                    (agora-rtm is 1.4MB, agora-chat 0.6MB) swaps the ENTIRE app for
                                    RouteChunkProgress until the SDK lands. Isolating them also makes
                                    it safe to defer their mount without blanking a painted page. */}
                                <Suspense fallback={null}>
                                  <RouteAwareFloatingIntercom />
                                </Suspense>
                                <Suspense fallback={null}>
                                  <ConditionalAgoraComponents />
                                </Suspense>
                                {apiDebugQueryConfig.debugPanelRequested && (
                                  <RestCustomServerChange floating={true} />
                                )}
                                <ApiDebugStatusBadge
                                  debugMode={debugMode}
                                  onExitDebugMode={onExitDebugMode}
                                />

                                <BookingRequestNavigationAndToastComponent />
                              </AppUpdatePopup>
                              {/* DocSideHelper rendered after RenderRoutes to prevent blocking page rendering */}
                              <DocSideHelper />
                            </DocSideContextProvider>
                          </NotificationContextProvider>
                        </ChamberScreenSignallingSnackBarProvider>
                      </FCMProvider>
                      {createPortal(
                        <GenericHideChildrenOnPrintWrapper>
                          <ToastContainer
                            position="top-center"
                            autoClose={5000}
                            hideProgressBar={true}
                            newestOnTop
                            closeOnClick
                            pauseOnFocusLoss
                            draggable
                            pauseOnHover
                          />
                        </GenericHideChildrenOnPrintWrapper>,
                        document.body,
                      )}

                      <SEOComponent
                        title={APP_CONSTANTS.DEFAULT_TITLE}
                        description={APP_CONSTANTS.DEFAULT_DESCRIPTION}
                        image={APP_CONSTANTS.APP_LOGO_LINK}
                      />
                    </>
                  </MuiLocalizationProvider>
                </ThemeDataContextProvider>
              </LocalizationContextProvider>
            </UnsecureSetupContextProvider>
          </ParallelDataProviders>
        </AuthContextProvider>
      </Suspense>
    </>
  )
}

const App = () => {
  const isClinicWalkinInitialRoute = window.location.pathname.startsWith('/clinic-walkin/')

  // Setup axios interceptors for 401 handling
  useEffect(() => {
    setupAxiosInterceptors()
  }, [])

  async function getIpAddressAndPutIntoLocalStorage() {
    try {
      const cached = localStorage.getItem('ipAddress')
      if (cached) {
        return
      }
      const ipAddress = await getIpAddress()
      if (ipAddress) {
        localStorage.setItem('ipAddress', ipAddress)
      }
    } catch (error) {
      console.warn('[App] Failed to get IP address:', error.message)
    }
  }

  useEffect(() => {
    const apiDomainConfig = resolveApiDomainConfig()
    if (apiDomainConfig.invalidEntries.length > 0) {
      console.warn('[API config] Ignoring invalid stored API URLs.', apiDomainConfig.invalidEntries)
    }

    console.info('[API config] Active API domains', {
      apiBaseUrl: apiDomainConfig.effectiveApiBaseUrl,
      apiSource: apiDomainConfig.apiSource,
      publicApiBaseUrl: apiDomainConfig.effectivePublicApiBaseUrl,
      publicApiSource: apiDomainConfig.publicApiSource,
    })
  }, [])

  const urlParams = new URLSearchParams(window.location.search)

  if (urlParams?.has('lang')) {
    sessionStorage.setItem('languageCode', urlParams.get('lang'))
  } else {
    const browserLanguage = navigator?.language || navigator?.userLanguage || 'en'
    sessionStorage.setItem(
      'languageCode',
      sessionStorage.getItem('languageCode') || browserLanguage.split('-')[0],
    )
  }

  if (!getSessionId()) {
    setSessionId(uuidv4())
  }

  if (!getSessionCreationTime()) {
    setSessionCreationTime(dayjs().format('YYYY-MM-DD HH:mm:ss'))
  }

  if (!localStorage.getItem('appInstallationId')) {
    let uniqueId = uuidv4()
    localStorage.setItem('appInstallationId', 'web' + uniqueId)
  }

  const DEBUG_MODE = localStorage.getItem('debugMode') === 'true'
  const apiDebugQueryConfig = getApiDebugQueryConfig()

  function stopDebugMode() {
    localStorage.setItem('debugMode', 'false')
    window.location.reload()
  } // making countrycode lowercase if it is not already

  // Suppress harmless third-party warnings and errors
  useEffect(() => {
    const originalError = console.error
    const originalWarn = console.warn
    
    // Override console.error to filter out harmless errors
    console.error = (...args) => {
      // Convert all arguments to string for checking
      const errorMessage = args.map(arg => {
        if (typeof arg === 'string') return arg
        if (arg?.toString) return arg.toString()
        return String(arg)
      }).join(' ')
      
      // Filter out Agora analytics CORS errors and referrer errors
      if (errorMessage.includes('webcollector-rtm.agora.io') || 
          errorMessage.includes('XMLHttpRequest cannot load') ||
          (errorMessage.includes('access control checks') && errorMessage.includes('agora')) ||
          errorMessage.includes('referrerNotAllowedMapError') ||
          (errorMessage.includes('referrer') && errorMessage.includes('agora'))) {
        // Suppress Agora telemetry failures and referrer restriction errors
        return
      }
      
      // Filter out Agora SDK permission errors (expected when permissions aren't granted yet)
      if ((errorMessage.includes('Agora-SDK') || errorMessage.includes('AgoraRTCException')) &&
          (errorMessage.includes('NotAllowedError') || errorMessage.includes('Permission denied')) &&
          (errorMessage.includes('track-mic') || errorMessage.includes('track-cam') || 
           errorMessage.includes('createMicrophoneAndCameraTracks'))) {
        // Suppress Agora SDK permission errors - these are expected when permissions haven't been granted
        // The app handles permissions separately through permission dialogs
        return
      }
      
      // Filter out styled-react-modal defaultProps warning (third-party library issue)
      if (errorMessage.includes('defaultProps will be removed from function components') ||
          errorMessage.includes('Support for defaultProps will be removed')) {
        return
      }
      
      // Filter out Razorpay 'web-share' feature warnings (can appear as errors too)
      if (errorMessage.includes("Unrecognized feature: 'web-share'") ||
          errorMessage.includes('web-share') ||
          errorMessage.includes('checkout.js')) {
        // Suppress Razorpay feature detection warnings
        return
      }
      // Suppress Razorpay checkout-static-next 403: their script sometimes requests build/undefined (internal quirk)
      if (errorMessage.includes('checkout-static-next.razorpay.com') ||
          (errorMessage.includes('403') && errorMessage.includes('razorpay') && errorMessage.includes('undefined'))) {
        return
      }
      
      // Filter out Google Maps API referer errors (configuration issue, not a code bug)
      if (errorMessage.includes('RefererNotAllowedMapError') ||
          errorMessage.includes('referer-not-allowed-map-error') ||
          errorMessage.includes('Your site URL to be authorized')) {
        // Suppress this error - it's a Google Cloud Console configuration issue
        // User needs to add http://localhost:3000/* to allowed referrers
        return
      }
      
      // Log all other errors normally
      originalError.apply(console, args)
    }
    
           // Override console.warn to filter out harmless warnings
           console.warn = (...args) => {
             // Convert all arguments to string for checking
             const warnMessage = args.map(arg => {
               if (typeof arg === 'string') return arg
               if (arg?.toString) return arg.toString()
               return String(arg)
             }).join(' ')
             
             // Filter out styled-react-modal defaultProps warning (third-party library issue)
             if (warnMessage.includes('defaultProps will be removed from function components') ||
                 warnMessage.includes('Support for defaultProps will be removed')) {
               return
             }
             
             // Filter out Razorpay 'web-share' feature warnings
             if (warnMessage.includes("Unrecognized feature: 'web-share'") ||
                 warnMessage.includes('web-share') ||
                 (warnMessage.includes('checkout.js') && warnMessage.includes('Unrecognized'))) {
               // Suppress this warning - it's from Razorpay's checkout script checking for Web Share API
               // This is harmless - Razorpay is just feature-detecting, and the API may not be available
               return
             }
             
             // Filter out Google Maps API loading warnings (we're already loading it async)
             if (warnMessage.includes('Google Maps JavaScript API has been loaded directly without loading=async') ||
                 warnMessage.includes('Google Maps') && warnMessage.includes('loading=async')) {
               // Suppress this warning - we're already loading the script asynchronously
               return
             }
             
             // Filter out Google Maps API referer errors (configuration issue, not a code bug)
             if (warnMessage.includes('RefererNotAllowedMapError') ||
                 warnMessage.includes('referer-not-allowed-map-error')) {
               // Suppress this error - it's a Google Cloud Console configuration issue
               // User needs to add http://localhost:3000/* to allowed referrers
               return
             }
             
             // Log all other warnings normally
             originalWarn.apply(console, args)
           }
    
    return () => {
      console.error = originalError
      console.warn = originalWarn
    }
  }, [])

  useEffect(() => {
    let countryCode = localStorage.getItem('countryCode')
    if (typeof countryCode === 'string') {
      const lowerCaseCountryCode = countryCode.toLowerCase()
      if (countryCode !== lowerCaseCountryCode) {
        localStorage.setItem('countryCode', lowerCaseCountryCode)
      }
    }
    if (!isClinicWalkinInitialRoute) {
      getIpAddressAndPutIntoLocalStorage()
    }
  }, [isClinicWalkinInitialRoute])

  return (
    <HelmetProvider>
      <BrowserRouter>
        <AppRouteTree
          apiDebugQueryConfig={apiDebugQueryConfig}
          debugMode={DEBUG_MODE}
          onExitDebugMode={stopDebugMode}
        />
      </BrowserRouter>
    </HelmetProvider>
  )
}

export default App

// Conditionally render Agora signaling components only on relevant routes
const ConditionalAgoraComponents = () => {
  const location = useLocation()
  const pathname = location?.pathname || ''

  // Chamber (video-call) screen signalling is Kiosk-portal only.
  // Note: Not needed on /ca-home/* (clinic assistants use FLOW_RECEPTION_TABLET which doesn't have SCREEN_DEVICE_CONFIG_ID)
  // Note: Not needed on /clinic-home/* (clinic owners/managers don't have isClinicAssistant or isChamberScreen roles)
  const shouldRenderChamberScreenComponent = pathname.startsWith('/kiosk/')

  // AUMHUM screen signalling runs on the Kiosk portal AND the Display (OOH)
  // portal, since the OOH/token screen relies on it for real-time updates.
  const shouldRenderAumhumScreenComponent =
    pathname.startsWith('/kiosk/') || pathname.startsWith('/display/')

  // Only render mentor component on mentor routes
  const shouldRenderMentorComponent =
    pathname.startsWith('/mentor-home/') || pathname.startsWith('/mentor/')

  const shouldRenderSupportComponent =
    pathname.startsWith('/support-home/') || pathname.startsWith('/support/')

  return (
    <>
      {shouldRenderChamberScreenComponent && <AgoraSignallingComponentForChamberScreens />}
      {shouldRenderAumhumScreenComponent && <AgoraSignallingComponentForAumhumScreens />}
      {shouldRenderMentorComponent && <AgoraSignallingComponentForMentors />}
      {shouldRenderMentorComponent && <EmrDebugCommandAgent />}
      {shouldRenderSupportComponent && <AgoraSignallingComponentForSupportPortalUsers />}
    </>
  )
}

const RenderRoutes = () => {
  const location = useLocation()
  const { unSecureSetupLoading, unSecureSetupData, unSecureSetupError } =
    useContext(UnsecureSetupContext)
  const { themeDataLoading, themeData, businessTheme, themeDataError } =
    useContext(ThemeDataContext)
  const { localizedDataLoading, localizedData, localizedDataError } =
    useContext(LocalizationContext)

  const { fcmStatus, notificationsAreDenied } = useContext(FCMContext)
  const [showPushNotificationDialog, setShowPushNotificationDialog] = useState(false)

  // Load route-specific CSS dynamically based on current route
  useEffect(() => {
    loadCSSForRoute(location.pathname).catch((error) => {
      console.warn('[App] Failed to load route-specific CSS:', error)
    })
  }, [location.pathname])

  useEffect(() => {
    const checkSupportAndShowPopup = async () => {
      if ((await isSupported()) && 'Notification' in window) {
        if (
          (fcmStatus === 'denied' && notificationsAreDenied) ||
          sessionStorage.getItem('notificationsAreDenied')
        ) {
          setShowPushNotificationDialog(false)
        } else {
          setShowPushNotificationDialog(fcmStatus !== 'granted')
        }
      } else {
        setShowPushNotificationDialog(false)
      }
    }
    checkSupportAndShowPopup()
  }, [fcmStatus, notificationsAreDenied])

  useEffect(() => {
    if (import.meta.env.VITE_ENV) {
      sessionStorage.setItem('env', import.meta.env.VITE_ENV)
    }
  }, [])

  function closePushNotificationDialog() {
    setShowPushNotificationDialog(false)
    setNotificationsAreDeniedLocal(true)
  }

  return (
    <>
      {/* Removed SplashScreen to avoid second loader - initial loader in index.html handles loading */}
      {/* Always show routes - initial loader will stay visible until data is loaded */}
      <>
        <Routes>
            {/* public pages :  */}

            <Route
              path="/clinic-walkin/:token"
              name="Clinic Walk-in QR Booking"
              element={
                <Suspense fallback={<ClinicWalkinRouteFallback />}>
                  <ClinicWalkinPage />
                </Suspense>
              }
            />

            {/* Public tracked Google-review link landing: resolves + hands the patient to their
                default browser. No auth/app chrome — self-contained. */}
            <Route
              path="/r/:token"
              name="Review Link Redirect"
              element={
                <Suspense fallback={null}>
                  <ReviewRedirectPage />
                </Suspense>
              }
            />

            {/* Public AUMHUM feedback form (mode-aware) — the fallback destination when a clinic has
                no Google review link configured. No auth/app chrome — self-contained. */}
            <Route
              path="/feedback/:token"
              name="Patient Feedback Form"
              element={
                <Suspense fallback={null}>
                  <ReviewFormPage />
                </Suspense>
              }
            />

            <Route
              path="/care-track/respond/:token"
              name="Care Track Questionnaire"
              element={
                <Suspense fallback={null}>
                  <CareTrackQuestionnairePage />
                </Suspense>
              }
            />

            <Route exact path="/login" name="Login Page" element={<Login />} />
            <Route exact path="/onboarding" name="Onboarding" element={<Onboarding />} />
            <Route
              path="/oauth/google-business/callback"
              name="Google Business OAuth"
              element={<GoogleBusinessOAuthCallback />}
            />
            {/* Phase 14b — public practitioner application surface. /apply (picker),
                /apply/:type (organic), /apply/mhp + /apply/mhp/:role (mental health
                professional sub-picker), /apply/i/:token (invitation-gated). */}
            <Route path="/apply/*" name="Apply" element={<PublicApplyApp />} />

            {/* Public QR validation of a medical certificate (OTP + captcha gated). */}
            <Route
              path="/verify-certificate/:certId"
              name="Verify Medical Certificate"
              element={
                <Suspense fallback={<div style={{ padding: 24 }}>Loading…</div>}>
                  <PublicCertificateValidationPage />
                </Suspense>
              }
            />

            {/* Legal, and Legacy things */}
            <Route exact path="/about" name="About" element={<AboutPage />} />
            <Route exact path="/privacy" name="Privacy" element={<PrivacyPage />} />
            <Route exact path="/terms" name="Terms" element={<TermsPage />} />
            <Route exact path="/cookie-policy" name="Terms" element={<CookiePolicy />} />
            <Route exact path="/instructor-terms" name="Terms" element={<InstructorTerms />} />
            <Route exact path="/legal" name="Legal" element={<LegalPage />} />

            {/* probably temp */}
            <Route
              exact
              path="/event-booking-successful"
              name="Booking Successful"
              element={<BookingSuccessfulPage />}
            />

            {/* Canonical privacy policy URL. The old `/privacy-policy-1`
                slug (an auto-generated Wix-era path) is kept below as a
                working legacy backup so existing inbound links and any
                already-published references don't break. */}
            <Route
              exact
              path="/privacy-policy"
              name="Privacy Policy"
              element={<PrivacyPolicyOne />}
            />
            {/* Legacy backup of the privacy policy — still serves the same
                content; its prerendered canonical points at /privacy-policy. */}
            <Route
              exact
              path="/privacy-policy-1"
              name="Privacy Policy (legacy)"
              element={<PrivacyPolicyOne />}
            />
            <Route
              exact
              path="/copy-of-privacy-policy"
              name="Privacy Policy 1"
              element={<CopyOfPrivacyPolicy />}
            />
            <Route
              exact
              path="/mental-health-medical-board/:id"
              name="Mental Health Medical Board"
              element={<MedicalBoardMemberPage />}
            />
            <Route
              exact
              path="/mental-health-medical-board"
              name="Mental Health Medical Board"
              element={<MentalHealthMedicalBoard />}
            />
            {/* ends : Legal, and Legacy things */}
            <Route exact path="/guest/:slug" name="Guests" element={<GuestMemberProfile />} />
            <Route
              exact
              path="/account/recover/:token"
              name="Change Password"
              element={<ChangePassword />}
            />
            <Route
              exact
              path="/forgot-password"
              name="Forgot Password"
              element={<ForgotPassword />}
            />
            <Route exact path="/help-center" name="Help Center" element={<HelpCenter />} />
            <Route
              exact
              path="/help-center-embedded"
              name="Help Center"
              element={<HelpCenter embedded={true} />}
            />
            <Route exact path="/download" name="Download AUMHUM" element={<DownloadAppPage />} />
            <Route
              exact
              path="/manage-data-and-account"
              name="Manage Data and Account"
              element={<ManageDataAndAccount />}
            />
            <Route
              exact
              path="/help-center/category/:categoryId"
              name="Help Center"
              element={<HelpCenterCategoryPage />}
            />
            <Route
              exact
              path="/help-center-embedded/category/:categoryId"
              name="Help Center"
              element={<HelpCenterCategoryPage embedded={true} />}
            />
            <Route exact path="/gift-card" name="Gift Card" element={<GiftCard />} />
            <Route exact path="/giftcard" name="Gift Card" element={<GiftCard />} />
            <Route exact path="/gift" name="Gift Card" element={<GiftCard />} />
            <Route exact path="/redeem" name="Gift Card Redeem" element={<GiftCardRedeem />} />
            <Route
              exact
              path="/gift-card/redeem"
              name="Gift Card Redeem"
              element={<GiftCardRedeem />}
            />
            <Route
              exact
              path="/offers/vf"
              name="VF Card Redeem Page"
              element={<GiftCardRedeem heading="Welcome VF Team" />}
            />
            <Route
              exact
              path="/swish-club"
              name="Swish Club - Redeem Page"
              element={
                <GiftCardRedeem
                  heading=" "
                  imageLink={getImageLink(APP_CONSTANTS.SWISH_CLUB_LOGO)}
                  subHeading={'Redeem your Swish Club code'}
                  inputPlaceholder={'Enter code here'}
                  seoTitle={'Redeem your code | AUMHUM & Swish Club'}
                  seoDescription={
                    'Enter your code to unlock benefits at AUMHUM | AUMHUM & Swish Club'
                  }
                  supportEmail={'swish-club-support@aumhum.com'}
                  addItemsToSessionStorage={{
                    acquisitionReferenceRefId: 'swish-club',
                    acquisitionReferenceRefType: 'type-affiliate',
                  }}
                  benefitsActivatedLine={'your Swish Club benefits have been activated.'}
                  showStepsToRedeemVideo={true}
                  stepsToRedeemVideoText={'Video Walkthrough'}
                  stepsToRedeemVideoLink={APP_CONSTANTS.SWISH_CLUB_CODE_REDEEM_VIDEO_LINK}
                  showFaqs={true}
                  blockRedemptionIfPremium={true}
                />
              }
            />
            <Route
              exact
              path="/clinic-invite/:inviteId"
              name="Account Invite Page"
              element={<AccountInvitePage />}
            />
            <Route
              exact
              path="/account/invite/:inviteId"
              name="Account Invite Page Alias"
              element={<AccountInvitePage />}
            />
            <Route
              exact
              path="/email/verify/:token"
              name="Email Verification"
              element={<EmailVerification />}
            />
            <Route
              exact
              path="/register"
              name="Register Page"
              element={<Navigate to="/onboarding" />}
            />
            <Route path="/reset-storage" element={<ResetStorage />} />
            <Route exact path="/404" name="Page 404" element={<Page404 />} />
            {/* <Route exact path="/403" name="Page 403" element={<AuthorityCheck />} /> */}
            {/* <Route exact path="/error" name="Page 500" element={<Page500 />} /> */}

            {/* webview pages :  */}

            <Route path="/webview/leaderboard" element={<LeaderboardPageWebview />} />
            <Route
              element={
                <MyLiveSessionsWrapperForWebview
                  pageViewType={bookedPageViewTypes.booked_sessions}
                />
              }
              path={`/webview/my-live-sessions/${bookedPageViewTypes.booked_sessions}/:eventId`}
            />
            <Route
              element={
                <MyLiveSessionsWrapperForWebview
                  pageViewType={bookedPageViewTypes.booked_sessions}
                />
              }
              path={`/webview/my-live-sessions/${bookedPageViewTypes.booked_sessions}`}
            />
            <Route
              element={<MyLiveSessionsWrapperForWebview pageViewType={bookedPageViewTypes.new} />}
              path={`/webview/my-live-sessions/${bookedPageViewTypes.new}`}
            />
            <Route element={<MyLiveSessionsWrapperForWebview />} path="/webview/my-live-sessions" />
            <Route element={<PatientHomeWrapperForWebview />} path={`/webview/patient-home`} />

            {/* ACTIVITY PLAN WEBVIEW ROUTES */}

            <Route element={<ActivityPlanListingPageWebview isWebView />} path="/webview/plan" />
            <Route element={<ActivityPlanPageWebView />} path="/webview/plan/:planId" />
            <Route element={<ActivityPlanPageWebView useId />} path="/webview/plan/id/:planId" />

            {/* ACTIVITY PLAN LISTING WEBVIEW ROUTES */}
            <Route element={<UserActivityListingPageWebview />} path="/webview/my-activity-plans" />
            <Route element={<ActivityPlanPageWebView />} path="/webview/plan/:planId" />
            <Route element={<ActivityPlanPageWebView useId />} path="/webview/plan/id/:planId" />

            {/* USER ACTIVITY PLAN WEBVIEW ROUTES */}
            <Route
              element={<UserActivityPageWebview idType="slug" />}
              path="/webview/my-activity/:planId"
            />

            <Route
              element={<UserActivityPageWebview idType="planId" />}
              path="/webview/my-activity/id/:planId"
            />
            {/* Public Live Session - Heavy Agora SDK is lazy loaded for this route */}
            {/* Error boundary catches chunk loading failures on slow networks */}
            {/* Skeleton shows immediately while the chunk loads */}
            <Route
              path="/v"
              element={
                <Suspense fallback={<PublicLiveSessionPageSkeleton />}>
                  <PublicSessionCodeJoinPage />
                </Suspense>
              }
            />
            <Route 
              path="/v/:sessionSlug" 
              element={
                <PublicLiveSessionPageErrorBoundary>
                  <Suspense fallback={<PublicLiveSessionPageSkeleton />}>
                    <PublicLiveSessionPage />
                  </Suspense>
                </PublicLiveSessionPageErrorBoundary>
              } 
            />

            {/* removeIntent pages:  */}
            <Route
              exact
              path="/mobileSignIn"
              name="Mobile SignIn Page"
              element={<MobileSignIn />}
            />

            {/* different flows : */}

            <Route
              path="/workplace/*"
              name="Business Home"
              element={
                <ThemeProvider theme={businessTheme}>
                  <BusinessProtectedRoute>
                    <BusinessLayout />
                  </BusinessProtectedRoute>
                </ThemeProvider>
              }
            />

            {/* clinic assistant flow: */}
            <Route
              path="/ca-home/*"
              name="Clinic Home"
              element={
                <ProtectedRoute>
                  <CALayout />
                </ProtectedRoute>
              }
            />

            {/* kiosk flow: */}
            <Route
              path="/kiosk/*"
              name="Kiosk"
              element={
                <ProtectedRoute>
                  <ClinicLayoutProvider>
                    <ClinicDataProvider
                      chosenRoleFlow="ROLE_AUMHUM_SCREEN"
                      assistantData={{
                        userProfileData: null,
                        associatedClinicsData: [],
                        associatedClinicChambersData: [],
                        clinicAssistantProfileData: [],
                        associatedChamberRoomsData: [],
                        associatedScreenDeviceConfigs: null,
                        associatedClinicAppointmentTypesData: null,
                      }}
                    >
                      <KioskRoutesHandler />
                    </ClinicDataProvider>
                  </ClinicLayoutProvider>
                </ProtectedRoute>
              }
            />

            {/* display (OOH / token) portal — ROLE_OOH: */}
            <Route
              path="/display/*"
              name="Display"
              element={
                <ProtectedRoute>
                  <ClinicLayoutProvider>
                    <ClinicDataProvider
                      chosenRoleFlow="ROLE_OOH"
                      assistantData={{
                        userProfileData: null,
                        associatedClinicsData: [],
                        associatedClinicChambersData: [],
                        clinicAssistantProfileData: [],
                        associatedChamberRoomsData: [],
                        associatedScreenDeviceConfigs: null,
                        associatedClinicAppointmentTypesData: null,
                      }}
                    >
                      <DisplayRoutesHandler />
                    </ClinicDataProvider>
                  </ClinicLayoutProvider>
                </ProtectedRoute>
              }
            />

            {/* support-portal OOH live preview (full-screen, support-scoped data): */}
            <Route
              path="/ooh-preview/:clinicLocationId/:mentorId"
              name="OOH Screen Preview"
              element={
                <ProtectedRoute>
                  <ClinicLayoutProvider>
                    <ClinicDataProvider
                      chosenRoleFlow="ROLE_AUMHUM_SCREEN"
                      assistantData={{
                        userProfileData: null,
                        associatedClinicsData: [],
                        associatedClinicChambersData: [],
                        clinicAssistantProfileData: [],
                        associatedChamberRoomsData: [],
                        associatedScreenDeviceConfigs: null,
                        associatedClinicAppointmentTypesData: null,
                      }}
                    >
                      <SupportOohPreview />
                    </ClinicDataProvider>
                  </ClinicLayoutProvider>
                </ProtectedRoute>
              }
            />

            {/* clinic assistant flow: */}
            <Route
              path="/support-home/*"
              name="Support Portal Home"
              element={
                <SupportPortalProtectedRoute>
                  <SupportPortalLayout />
                </SupportPortalProtectedRoute>
              }
            />

            {/* chamber screen flow:
            <Route
              path="/chamber-screen-home/*"
              name="Chamber Screen Home"
              element={
                <ProtectedRoute>
                  <KioskLayout />
                </ProtectedRoute>
              }
            /> */}

            {/* clinic owner flow/ clinic prospect/ clinic admin: */}
            <Route
              path="/clinic-home/*"
              name="Clinic Home"
              element={
                <ProtectedRoute>
                  <ClinicOwnerLayout />
                </ProtectedRoute>
              }
            />

            {/* needed here: doctor home and therapist home: */}
            <Route
              path="/mentor-home/*"
              name="Mentor Home"
              element={
                <ProtectedRoute>
                  <MentorLayout />
                </ProtectedRoute>
              }
            />

            {/* patient portal — default home for ROLE_PATIENT users.
                Standalone clinical-themed portal at /patient-home/* with its own
                multi-patient identity model. See docs/PATIENT_PORTAL_PLAN.md. */}
            <Route
              path="/patient-home/*"
              name="Patient Home"
              element={
                <PatientPortalProtectedRoute>
                  <PatientPortalLayout />
                </PatientPortalProtectedRoute>
              }
            />

            {/* IPD (in-patient) portal — daily workplace for ward staff
                (ROLE_IPD_MANAGER / FRONT_DESK / NURSE / STAFF).
                See docs/INPATIENT_MANAGEMENT_PLAN.md §8. */}
            <Route
              path="/ipd-home/*"
              name="IPD Home"
              element={
                <IpdPortalProtectedRoute>
                  <IpdLayout />
                </IpdPortalProtectedRoute>
              }
            />

            <Route
              path="*"
              name="Consumer Layout"
              element={
                <ThemeProvider theme={themeData || {}}>
                  <ConsumerLayout />
                </ThemeProvider>
              }
            />

            {/* removeIntent pages:  */}
            {/* <Route path="/userProfile/*" name="Profile" element={<ProfileLayout />} /> */}
          </Routes>
          <Dialog
            // open={showPushNotificationDialog}
            open={false}
            onClose={closePushNotificationDialog}
            PaperComponent={StyledPushNotificationDialog}
          >
            <Suspense fallback={<CircularProgress />}>
              <LazyPushNotificationComponent closeFunction={closePushNotificationDialog} />
            </Suspense>
            {/* <PushNotificationComponent closeFunction={closePushNotificationDialog} /> */}
          </Dialog>
          {/* Global 401 Login Modal - handles expired token scenarios */}
          <Global401LoginModal />
        </>
    </>
  )
}
